(ns app.front.view.business.view
  (:require [app.domain.business.model :as business-model]
            [app.domain.contractor-registration.model :as cr-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.licence.generic-model :as gl-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.task :as task]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.cards :as ui-cards]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.email-toggle :as ui-email-toggle]
            [app.front.ui.multi-select :as ms]
            [app.front.ui.section :as section]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.bond.list :as bond-list]
            [app.front.view.business.information :as business-info-form]
            [app.front.view.business.subs :as business-subs]
            [app.front.view.escrow.account-list :as escrow-account-list]
            [app.front.view.letter.section :as letter-section]
            [app.front.view.licence.table :as licence-table]
            [app.front.view.licence.util :as licence-util]
            [app.front.view.party.common :as party-common]
            [app.front.view.property.header :as property-header]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(defn registration-href [cr]
  (href/href :view/contractor-registration {:cr-id (:contractor-registration/id cr)}))

(defn trade-names [lang cr]
  (->> cr
       cr-model/contractor-registration-trades
       (map (fn [trade]
              (-> trade
                  :registered-trade/work-type
                  (config-model/localized-name lang))))
       sort
       (str/join ", ")))

(defn- licence-card [{:keys [tr lang]}
                     {:licence/keys [category-group-config revocation-ts] :as licence}]
  (let [valid-to     (gl-model/get-valid-to licence)
        valid?       (licence-util/valid? licence)
        expires-soon (licence-util/licence-expires-soon? licence)
        date-fn      (<sub [::date-util/date-fn])
        format-date  (fn [date]
                       (licence-table/format-date date-fn licence date))]
    [:<>
     [ui-cards/card {:href     (licence-table/href licence)
                     :title    (config-model/localized-name category-group-config lang)
                     :subtitle (case (:domain/state licence)
                                 :licence.state/revoked
                                 (str (tr [:licence/revoked-on]) " " (format-date revocation-ts))
                                 :licence.state/expired
                                 (str (tr [:licence/expired-on]) " " (format-date valid-to))
                                 (if valid-to
                                   (str (tr [(if valid? :licence/expires-on :licence/expired-on)]) " " (format-date valid-to))
                                   (tr [:licence/expires-never])))
                     :pill     (cond
                                 (not valid?) {:kind :disabled
                                               :text (tr [(:domain/state licence)])}
                                 expires-soon {:kind :warning
                                               :text (tr [:licence/expires-soon])})}]]))

(defn- registration-card [{:keys [tr lang]}
                          {:contractor-registration/keys [revocation-ts] :as cr}]
  (let [valid-to     (gl-model/get-valid-to cr)
        valid?       (du/in-state? :contractor-registration.state/active cr)
        expires-soon (licence-util/licence-expires-soon? cr)
        date-fn      (<sub [::date-util/date-fn])
        format-date  (fn [date]
                       (licence-table/format-date date-fn cr date))]
    [:<>
     [ui-cards/card {:href     (registration-href cr)
                     :title    (trade-names lang cr)
                     :subtitle (case (:domain/state cr)
                                 :contractor-registration.state/revoked
                                 (str (tr [:licence/revoked-on]) " " (format-date revocation-ts))
                                 :contractor-registration.state/expired
                                 (str (tr [:licence/expired-on]) " " (format-date valid-to))
                                 (if valid-to
                                   (str (tr [(if valid? :licence/expires-on :licence/expired-on)]) " " (format-date valid-to))
                                   (tr [:licence/expires-never])))
                     :pill     (cond
                                 (not valid?) {:kind :disabled
                                               :text (tr [(:domain/state cr)])}
                                 expires-soon {:kind :warning
                                               :text (tr [:licence/expires-soon])})}]]))


(defn- licence-cards [{:keys [tr lang]} licences]
  [ui-cards/cards-container
   (for [licence licences]
     ^{:key (:db/id licence)} [licence-card (util/mmap tr lang) licence])])

(defn- registration-cards [{:keys [tr lang]} registrations]
  [ui-cards/cards-container
   (for [cr registrations]
     ^{:key (:db/id cr)} [registration-card (util/mmap tr lang) cr])])


(defn- licences-table [{:keys [tr lang]} licences]
  [ui-table/modern-responsive-table
   {:class      "w-full mt-2"
    :cols       [[:licence/type
                  (fn [{:licence/keys [category-group-config]}]
                    (config-model/localized-name category-group-config lang))]

                 (licence-table/state-column tr)

                 [:licence/address
                  (fn [{:licence/keys [primary-property]}]
                    (property-header/address-with-municipality-name tr primary-property))]

                 licence-table/issue-date-column
                 licence-table/expiry-date-column]
    :link       {:make-href   licence-table/href
                 :description :licence/go-to}
    :get-status licence-table/get-status
    :items      licences
    :tr         tr}])

(defn- registrations-table [{:keys [tr lang]} registrations]
  [ui-table/modern-responsive-table
   {:class      "w-full mt-2"
    :cols       [[:contractor-registration/trades
                  (partial trade-names lang)]
                 (licence-table/state-column tr)
                 licence-table/issue-date-column
                 licence-table/expiry-date-column]
    :link       {:make-href   registration-href
                 :description :contractor-registration/go-to}
    :get-status licence-table/get-status
    :items      registrations
    :tr         tr}])


(defn- licence-list [{:keys [tr lang view-type]} licences]
  (case view-type
    :view/cards [licence-cards (util/mmap tr lang) licences]
    :view/list  [licences-table (util/mmap tr lang) licences]))

(defn- registration-list [{:keys [tr lang view-type]} registrations]
  (case view-type
    :view/cards [registration-cards (util/mmap tr lang) registrations]
    :view/list  [registrations-table (util/mmap tr lang) registrations]))


(defn- render-valid-licences [component {:keys [tr lang title view-type]} licences]
  [:div.bx--row.mt-4>div.bx--col-xs-12
   [:h3.mb-0.mb-4 title]
   [component (util/mmap tr lang view-type) licences]])


(defn- render-invalid-licences [component {:keys [tr lang show-invalid view-type id]} licences]
  (when licences
    [:div.bx--row.mt-8>div.bx--col-xs-12
     [ui/expand-button {:title-expanded  (tr [:licence/expireds-hide])
                        :title-collapsed (tr [:licence/expireds-show])
                        :expanded?       show-invalid
                        :tr              tr
                        :expand-handler  (fn [_] (>evt [::business-subs/toggle-invalid id]))}]
     (when show-invalid
       [:div.mt-6.animation-appear-slide-down
        [component (util/mmap tr lang view-type) licences]])]))


(defn render-all-licences [component {:keys [id tr lang title]} {:keys [valid invalid]}]
  (let [show-invalid (<sub [::business-subs/show-invalid? id])
        view-type    (<sub [::business-subs/licences-view-type id])]
    [:<>
     [render-valid-licences component (util/mmap tr lang view-type title) valid]
     [render-invalid-licences component (util/mmap tr id lang show-invalid view-type) invalid]]))


(def ^:private licences-views [:view/cards, :view/list])


(defn- view-type-toggle [{:keys [tr id]}]
  [ui-cs/content-switcher {:class     "mr-4"
                           :items     licences-views
                           :val->str  #(tr [%])
                           :current   (<sub [::business-subs/licences-view-type id])
                           :on-change #(>evt [::business-subs/set-licences-view-type id %])}])


(defn- licences-section [{:keys [business-id tr lang]}]
  (let [id           ::licences
        licences     (<sub [::business-subs/analyzed-business-licences business-id])
        show-invalid (<sub [::business-subs/show-invalid? id])]
    (if (<sub [::business-subs/licences-loading?])
      [section/section-skeleton]
      (when (seq licences)
        [section/section "business-licences"
         {:heading-extra [view-type-toggle (util/mmap tr id)]
          :tr            tr}
         :business/licences
         [render-all-licences
          licence-list
          (util/mmap {:title (tr [(if show-invalid :licence/valids :licence/licences)])}
                     id tr lang)
          licences]]))))


(defn- registrations-section [{:keys [business-id tr lang]}]
  (let [id            ::registrations
        registrations (<sub [::business-subs/analyzed-business-registrations business-id])]
    (if (<sub [::business-subs/licences-loading?])
      [section/section-skeleton]
      (when (seq registrations)
        [section/section "business-registrations"
         {:heading-extra [view-type-toggle (util/mmap tr id)]
          :tr            tr}
         :business/registrations
         [render-all-licences
          registration-list
          (util/mmap {:title (tr [:contractor-registration/contractor-registrations])} id tr lang)
          registrations]]))))

(defn work-type-target-text [tr lang app]
  (let [work-types  (:application/work-type-group-configs app)
        work-target (:application/work-target-group-config app)]
    (if (-> work-types rest seq)
      (tr [:search/multiple-work-types])
      (->> (conj work-types work-target)
           (remove nil?)
           (map #(config-model/localized-name % lang))
           (str/join " / ")))))

(defn workspaces-table [{:keys [tr lang]} workspaces]
  (let [format-date (<sub [::date-util/date-fn])]
    (for-frag [[permit-type workspaces] workspaces]
      [section/subsection
       {:title (tr [permit-type])}
       [ui-table/modern-responsive-table
        {:items workspaces
         :link  {:description ws-util/workspace-link-description
                 :make-href   ws-util/workspace-or-resume-application-href}
         :tr    tr
         :cols  [[:workspace/municipality
                  (comp #(municipality-model/municipality-name % lang)
                        :workspace/municipality)]
                 [:workspace/address
                  :workspace/address]
                 [:workspace/category
                  #(-> % :workspace/category-group-config (config-model/localized-name lang))]
                 [:business/work-type-target
                  (fn [workspace]
                    (->> workspace
                         workspace-model/workspace-application
                         (work-type-target-text tr lang)))]
                 [:domain/state
                  #(tr [(-> % workspace-model/workspace-target :domain/state)])]
                 [:verdict/issue-date
                  (fn [workspace]
                    (-> workspace
                        workspace-model/workspace-application
                        verdict-model/single-verdict
                        :verdict/issue-date
                        (date-util/to-tz (workspace-model/get-tz workspace))
                        format-date))]]}]])))

(defn workspaces-section [{:keys [tr lang business-id]}]
  (if (<sub [::business-subs/workspaces-loading?])
    [section/section-skeleton]
    (let [all-workspaces (<sub [::business-subs/workspaces business-id])]
      (when (seq all-workspaces)
        [section/section
         "business-workspaces"
         (util/mmap tr)
         :business/workspaces
         [workspaces-table
          (util/mmap tr lang)
          all-workspaces]]))))


(defn add-user [{:keys [tr business-id]}]
  (r/with-let [*groups          (r/atom #{})
               *user-email      (r/atom "")
               on-groups-change #(reset! *groups %)]
    (let [groups    (<sub [::business-subs/user-groups business-id])
          group-ids (-> groups keys)]
      [ui/modal-confirmation-button
       {:id                  "add-user-modal-btn"
        :trigger-button-text (tr [:business.users/add-user])
        :no-button-text      (tr [:cancel])
        :yes-button-text     (tr [:business.users/add-user])
        :trigger-button-opts {:kind :tertiary
                              :icon i/user-plus}
        :yes-disabled?       (or (empty? @*groups) (str/blank? @*user-email))
        :on-yes-click        (fn []
                               (>evt [::business-subs/invite-user-to-business
                                      business-id
                                      @*user-email
                                      @*groups])
                               (reset! *user-email ""))
        :tr                  tr}
       [:div
        [:div.mb-8
         [:div.mb-2 (tr [:business.users/add-groups-help])]
         [ms/multi-checkbox
          {:val->str  (fn [group-id]
                        (-> groups (get group-id) :group/name))
           :values    group-ids
           :on-change on-groups-change
           :selected  @*groups
           :tr        tr}]]
        [ui/text-input
         {:label-text (tr [:user/email])
          :value      @*user-email
          :on-change  #(reset! *user-email (futil/tval %))
          :type       :email}]]])))


(defn edit-groups-modal [{:keys [tr business-id]}]
  (let [{:keys [group-ids
                user-id]} (<sub [::business-subs/edit-groups-modal-data])
        groups            (<sub [::business-subs/user-groups business-id])
        all-group-ids     (-> groups keys)
        current-user-id   (<sub [:session/user-id])]
    [ui/confirm-modal
     {:id              "edit-groups-modal"
      :open?           (<sub [::business-subs/edit-groups-modal-open?])
      :yes-disabled?   (empty? group-ids)
      :on-close        #(>evt [::business-subs/cancel-edit-groups])
      :on-yes-click    #(>evt [::business-subs/update-user-groups
                               business-id
                               user-id
                               group-ids])
      :modal-title     (tr [:business.users/edit-groups])
      :no-button-text  (tr [:cancel])
      :yes-button-text (tr [:save])
      :tr              tr}
     [:div.flex.flex-col
      [ms/multi-checkbox
       {:id        "edit-groups"
        :val->str  (fn [group-id]
                     (-> groups (get group-id) :group/name))
        :values    all-group-ids
        :on-change #(>evt [::business-subs/set-groups-in-edit %])
        :selected  group-ids
        :disabled  (when (= user-id current-user-id)
                     (->> groups
                          ;; TODO wish we had a role for admin groups, not just authorizations
                          (filter #(-> % second :group/name (= business-model/admin-group-name)))
                          ffirst
                          hash-set))
        :tr        tr}]]]))

(defn make-actions-fn [tr business-id]
  (let [user-id (<sub [:session/user-id])]
    (fn [{:keys                [:db/id :entity/type]
          :business.users/keys [status name-or-email group-ids]}]
      (concat
        [(when (not= id user-id)
           (let [title (tr [(case type
                              :entity.type/user :business.users/remove-user
                              :entity.type/task (case status
                                                  :business.users.status/declined :remove
                                                  :business.users/withdraw-invite)
                              nil)])]
             {:title           title
              :confirm?        true
              :confirm-content [:div
                                title
                                " "
                                name-or-email]
              :icon            i/remove
              :on-click        #(>evt [(case type
                                         :entity.type/user ::business-subs/remove-user
                                         :entity.type/task ::business-subs/withdraw-invite)
                                       (util/mmap business-id id)])}))
         (when (= type :entity.type/user)
           {:title    (tr [:business.users/edit-groups])
            :icon     i/pen
            :on-click #(>evt [::business-subs/show-edit-groups-modal business-id id group-ids])})]))))

(defn users-section [{:keys [tr business-id]}]
  (when (<sub [::business-subs/show-users-section? business-id])
    [:<>
     [section/section
      :business-users
      {:heading-button (when (<sub [::business-subs/user-is-admin? business-id])
                         [add-user (util/mmap tr business-id)])
       :tr             tr}
      (tr [:business/users])
      [ui-table/modern-responsive-table
       (util/mmap {:cols         [[(tr [:business.users/status])
                                   (fn [{:business.users/keys [status loading?]}]
                                     [:div.flex.gap-4
                                      (tr [status])
                                      (when loading? (cc/loading-spinner {:small true :with-overlay false}))])]
                                  [(tr [:business.users/name]) :business.users/name]
                                  [(tr [:business.users/email]) :business.users/email]
                                  [(tr [:business.users/groups]) :business.users/groups]]
                   :items        (<sub [::business-subs/users-and-invites business-id])
                   :get-status   #(case (:business.users/status %)
                                    :business.users.status/active   :ok
                                    :business.users.status/invited  :task
                                    :business.users.status/declined :warning
                                    :warning)
                   :make-actions (when (<sub [::business-subs/user-is-admin? business-id])
                                   (make-actions-fn tr business-id))}
                  tr)]
      (when-let [emails (<sub [::business-subs/invites-pending-emails])]
        [:div.flex.gap-4
         (cc/loading-spinner {:with-overlay false :small true})
         (tr [:business/invites-pending] [emails])])]
     [edit-groups-modal (util/mmap tr business-id)]]))

(defn edit-buttons [tr business editing?]
  (when (<sub [::business-subs/user-is-admin? (:db/id business)])
    [:div.flex.flex-row.justify-end.pt-1.gap-2
     (if editing?
       (let [business-form (<sub [::business-subs/info-edit-form])
             valid?        (<sub [::business-subs/form-valid?])]
         [ui-action-buttons/save-cancel-buttons
          {:on-cancel      #(>evt [::business-subs/cancel-info-editing])
           :on-save        #(>evt [::business-subs/update-business business business-form])
           :save-disabled? (not valid?)
           :size           :small
           :tr             tr}])
       (let [default-country (<sub [::front-config/country-kw])]
         (cc/button {:kind             :ghost
                     :size             :small
                     :icon             i/pen
                     :icon-description (tr [:edit])
                     :icon-position    :right
                     :on-click         (fn [_]
                                         (>evt [::business-subs/edit-info
                                                (-> (select-keys business
                                                                 business-model/business-attrs)
                                                    (update :business/mailing-country (fnil identity default-country)))])
                                         (>evt [::party-common/set-doc-value [::party-common/show-errors?] true]))}
                    (tr [:edit]))))]))


(defn edited-timestamp [entity {:keys [edited-text created-text]}]
  (let [format-date-time (<sub [::date-util/date-time-fn])]
    (cond
      (:latest-change entity)
      (str/join " " [edited-text
                     (-> entity :latest-change :user)
                     (format-date-time (-> entity :latest-change :ts))])

      (:entity/creator entity)
      (str/join " " [created-text
                     (let [{:user/keys [first-name last-name]} (:entity/creator entity)]
                       (str first-name " " last-name))
                     (format-date-time (:entity/created entity))]))))

(defn business-information [tr business]
  (let [editing?    (<sub [::business-subs/info-editing?])
        lang        (<sub [:language])
        validator   (<sub [::business-subs/validator-fn])
        schema      business-model/BusinessEditInfo
        business-id (:db/id business)]
    (if business
      [:section.flex.flex-col.w-full
       [:h1.font-bold.mb-3.text-ui-night-sky-100
        (-> business
            :business/names
            (i18n-model/get-text-from-entities-or-map lang))]

       [:div.mb-4.flex.sm:gap-16.flex-wrap
        (->> [(:business/mailing-address business)
              (:business/municipality business)
              (tr [(keyword "subdivision" (name (:business/subdivision business)))])]
             (remove nil?)
             (str/join ", "))
        (when (<sub [::business-subs/is-member? business-id])
          [:div
           [ui-email-toggle/email-toggle
            {:tr                     tr
             :notifications-enabled? (<sub [::business-subs/notifications-enabled? business-id])
             :on-toggle              #(>evt [::business-subs/set-email-notifications business-id %])}]])]

       [ui-sep/horizontal]

       [:div.mt-4.flex.justify-between
        [:h2.uppercase.font-semibold.text-lg (tr [:business/contact-information])]
        [edit-buttons tr business editing?]]
       (if editing?
         (let [on-change      #(>evt [::business-subs/update-info-value %1 %2])
               on-change-sync #(rf/dispatch-sync (if (and (string? %2) (str/blank? %2))
                                                   [::business-subs/dissoc-info-value %1]
                                                   [::business-subs/update-info-value %1 %2]))
               value-fn       #(<sub [::business-subs/info-edit-form-field %])]
           [business-info-form/business-info-form (util/mmap {:existing-business business}
                                                             validator tr schema on-change on-change-sync value-fn)])
         [business-info-form/read-only-form tr business])
       [:div.flex.justify-end.my-4.italic
        [edited-timestamp business {:created-text (tr [:business/created-by])
                                    :edited-text  (tr [:business/edited-by])}]]]
      [section/section-skeleton])))

(defn visibility-help [{:keys [tr permissions]}]
  (case [(<sub [::business-subs/visibility])
         (<sub [:session/authority-user?])
         (contains? permissions :permission.business/write)]
    [:business.read-access/limited true false]
    [ui/help (tr [:business/limited-visibility-help-authority])]
    [:business.read-access/limited false false]
    [ui/help (tr [:business/limited-visibility-help-user])]
    [:business.read-access/full true false]
    [ui/help (tr [:business/full-visibility-help-authority])]
    [:business.read-access/full true true]
    [ui/help (tr [:business/temporary-edit-help-authority])]
    nil))

(defn dashlet-id [business-id] (str "business-" business-id "-tasks"))

(defn view [{{{:keys [business-id]} :path} :parameters}]
  (let [lang        (<sub [:language])
        tr          (<sub [:tr])
        business-id (parse-long business-id)
        business    (<sub [::entities/entity business-id])]
    [:div.cloudpermit-page
     [visibility-help (util/mmap {:permissions (:granted-permissions business)} tr)]
     [business-information tr business]
     [task/task-dashlet {:id               (dashlet-id business-id)
                         :business-id      business-id
                         :heading-i18n-key :business/tasks-heading}]
     [bond-list/bond-list (util/mmap {:title (tr [:bond/bonds]) :owner-entity-id business-id} tr)]
     [licences-section (util/mmap {:business-id (:db/id business)} tr lang)]
     [registrations-section (util/mmap {:business-id (:db/id business)} tr lang)]
     [workspaces-section (util/mmap tr lang business-id)]
     [users-section (util/mmap tr business-id)]
     [escrow-account-list/account-list (util/mmap {:owner-entity-id business-id} tr)]
     [letter-section/letters-section (util/mmap {:target-id business-id} tr)]]))

(defn breadcrumb [{:keys [match]}]
  (let [{:keys [business-id]
         :as   params}       (-> match :parameters :path)
        business-name        (<sub [::business-subs/name (parse-long business-id)])]
    [:view/business params business-name]))

(def route
  {:name        :view/business
   :view        view
   :breadcrumb  {:items [breadcrumb]}
   :controllers [{:parameters {:path [:business-id]}
                  :start      (fn [{{:keys [business-id]} :path}]
                                (>evt [::business-subs/fetch-business business-id (dashlet-id business-id)])
                                (>evt [::business-subs/fetch-licences business-id])
                                (>evt [::business-subs/fetch-workspaces business-id]))}]})
